import { Vector2 } from '@babylonjs/core/Maths/math';
import { SVGPathData } from 'svg-pathdata';
import {
    CommandA,
    CommandC,
    CommandH,
    CommandL,
    CommandM,
    CommandQ,
    CommandS,
    CommandT,
    CommandV,
    CommandZ,
    SVGCommand,
} from 'svg-pathdata/lib/types';

export type CommandType = SVGCommand['type'];
// export type Command = { type: CommandType } & Omit<CommandM, 'type'> &
//     Omit<CommandL, 'type'> &
//     Omit<CommandH, 'type'> &
//     Omit<CommandV, 'type'> &
//     Omit<CommandZ, 'type'> &
//     Omit<CommandQ, 'type'> &
//     Omit<CommandT, 'type'> &
//     Omit<CommandC, 'type'> &
//     Omit<CommandS, 'type'> &
//     Omit<CommandA, 'type'>;

export type Command =
    | CommandA
    | CommandC
    | CommandH
    | CommandL
    | CommandM
    | CommandQ
    | CommandS
    | CommandT
    | CommandV
    | CommandZ;

export class PathParser extends SVGPathData {
    control: Vector2 = new Vector2(0, 0);
    start: Vector2 = new Vector2(0, 0);
    current: Vector2 = new Vector2(0, 0);
    command: Command | null = null;
    override readonly commands: Command[] /* Babel fix: */ = this.commands;
    private i = -1;
    private previousCommand: Command | null = null;
    private points: Vector2[] = [];
    private angles: Array<number | null> = [];

    constructor(path: string) {
        super(
            path
                // Fix spaces after signs.
                .replace(/([+\-.])\s+/gm, '$1')
                // Remove invalid part.
                .replace(/[^MmZzLlHhVvCcSsQqTtAae\d\s.,+-].*/g, '')
        );
    }

    reset() {
        this.i = -1;
        this.command = null;
        this.previousCommand = null;
        this.start = new Vector2(0, 0);
        this.control = new Vector2(0, 0);
        this.current = new Vector2(0, 0);
        this.points = [];
        this.angles = [];
    }

    isEnd() {
        const { i, commands } = this;

        return i >= commands.length - 1;
    }

    next() {
        const command = this.commands[++this.i];

        this.previousCommand = this.command;
        this.command = command;

        return command;
    }

    getPoint(xProp = 'x', yProp = 'y') {
        if (this.command) {
            const x = parseInt(xProp);
            const y = parseInt(yProp);
            const point = new Vector2(x, y);
            return this.makeAbsolute(point);
        }
    }

    getAsControlPoint(xProp?: string, yProp?: string) {
        const point = this.getPoint(xProp, yProp);
        if (point) {
            this.control = point;
        }

        return point;
    }

    getAsCurrentPoint(xProp?: string, yProp?: string) {
        const point = this.getPoint(xProp, yProp);
        if (point) {
            this.current = point;
        }

        return point;
    }

    getReflectedControlPoint() {
        let previousCommand = null;
        if (this.previousCommand) {
            previousCommand = this.previousCommand.type;
        }

        if (
            previousCommand !== SVGPathData.CURVE_TO &&
            previousCommand !== SVGPathData.SMOOTH_CURVE_TO &&
            previousCommand !== SVGPathData.QUAD_TO &&
            previousCommand !== SVGPathData.SMOOTH_QUAD_TO
        ) {
            return this.current;
        }

        // reflect point
        const {
            current: { x: cx, y: cy },
            control: { x: ox, y: oy },
        } = this;
        const point = new Vector2(2 * cx - ox, 2 * cy - oy);

        return point;
    }

    makeAbsolute(point: Vector2) {
        if (this.command && 'relative' in this.command && this.command.relative) {
            const { x, y } = this.current;

            point.x += x;
            point.y += y;
        }

        return point;
    }

    addMarker(point: Vector2, from?: Vector2, priorTo?: Vector2) {
        const { points, angles } = this;

        // if the last angle isn't filled in because we didn't have this point yet ...
        if (priorTo && angles.length > 0 && !angles[angles.length - 1]) {
            angles[angles.length - 1] = this.angleBetweenPoints(points[points.length - 1], priorTo);
        }

        this.addMarkerAngle(point, from ? this.angleBetweenPoints(from, point) : null);
    }

    addMarkerAngle(point: Vector2, angle: number | null) {
        this.points.push(point);
        this.angles.push(angle);
    }

    angleBetweenPoints(point1: Vector2, point2: Vector2) {
        return Math.atan2(point2.y - point1.y, point2.x - point1.x);
    }

    getMarkerPoints() {
        return this.points;
    }

    getMarkerAngles() {
        const { angles } = this;
        const len = angles.length;

        for (let i = 0; i < len; i++) {
            if (!angles[i]) {
                for (let j = i + 1; j < len; j++) {
                    if (angles[j]) {
                        angles[i] = angles[j];
                        break;
                    }
                }
            }
        }

        return angles;
    }
}
